import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import {
  MONTHS,
  MONTH_LABELS,
  type SeasonEntry,
  type SeasonalParams,
  type RuleFormProps,
  majorStrToMinorOpt,
  minorToMajorStr,
  pctStrToNumOpt,
  intStrOpt,
} from '@/lib/pricingRules';

// seasonal: a list of recurring yearly date ranges ("MM-DD", no year — a
// range may wrap, e.g. 12-25 → 01-03), each with an optional %/absolute
// adjustment and an optional min-stay. Mirrors the local string-draft model
// from DateSpecificRuleForm (the reference form), except from/to are edited
// as a month Select + day number input pair and recombined into "MM-DD" on
// every change.

const MONTH_OPTIONS = MONTHS.map((m) => ({ value: String(m), label: MONTH_LABELS[m] }));

interface EntryDraft {
  fromMonth: string;
  fromDay: string;
  toMonth: string;
  toDay: string;
  pct: string;
  abs: string;
  minStay: string;
  minPrice: string;
  maxPrice: string;
}

function parseMD(md: string): { month: string; day: string } {
  const [mRaw, dRaw] = (md ?? '').split('-');
  const m = Number.parseInt(mRaw ?? '', 10);
  const d = Number.parseInt(dRaw ?? '', 10);
  return {
    month: Number.isFinite(m) && m >= 1 && m <= 12 ? String(m) : '1',
    day: Number.isFinite(d) && d >= 1 && d <= 31 ? String(d) : '1',
  };
}

function toMD(month: string, day: string): string {
  const m = Number.parseInt(month, 10);
  const d = Number.parseInt(day, 10);
  const mm = Number.isFinite(m) && m >= 1 && m <= 12 ? m : 1;
  const dd = Number.isFinite(d) && d >= 1 && d <= 31 ? d : 1;
  return `${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`;
}

function entryToDraft(e: SeasonEntry): EntryDraft {
  const from = parseMD(e.from);
  const to = parseMD(e.to);
  return {
    fromMonth: from.month,
    fromDay: from.day,
    toMonth: to.month,
    toDay: to.day,
    pct: e.pct != null ? String(e.pct) : '',
    abs: minorToMajorStr(e.abs_minor),
    minStay: e.min_stay != null ? String(e.min_stay) : '',
    minPrice: minorToMajorStr(e.min_price_minor),
    maxPrice: minorToMajorStr(e.max_price_minor),
  };
}

function draftToEntry(d: EntryDraft): SeasonEntry {
  const e: SeasonEntry = {
    from: toMD(d.fromMonth, d.fromDay),
    to: toMD(d.toMonth, d.toDay),
  };
  const p = pctStrToNumOpt(d.pct);
  if (typeof p === 'number') e.pct = p;
  const a = majorStrToMinorOpt(d.abs);
  if (typeof a === 'number') e.abs_minor = a;
  const ms = intStrOpt(d.minStay);
  if (typeof ms === 'number') e.min_stay = ms;
  const lo = majorStrToMinorOpt(d.minPrice);
  if (typeof lo === 'number') e.min_price_minor = lo;
  const hi = majorStrToMinorOpt(d.maxPrice);
  if (typeof hi === 'number') e.max_price_minor = hi;
  return e;
}

export function SeasonalRuleForm({ params, onChange }: RuleFormProps<SeasonalParams>) {
  const [drafts, setDrafts] = useState<EntryDraft[]>(() =>
    (params.seasons ?? []).map(entryToDraft),
  );

  function commit(next: EntryDraft[]) {
    setDrafts(next);
    onChange({ seasons: next.map(draftToEntry) });
  }

  function patch(i: number, p: Partial<EntryDraft>) {
    commit(drafts.map((d, idx) => (idx === i ? { ...d, ...p } : d)));
  }

  return (
    <div className="flex flex-col gap-3">
      {drafts.map((d, i) => (
        <div
          key={i}
          className="rounded-[var(--radius-control)] border border-[var(--border-hairline)] bg-[var(--surface-sunken)] p-3"
        >
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="grid grid-cols-2 gap-2">
              <Select
                label="From month"
                options={MONTH_OPTIONS}
                value={d.fromMonth}
                onChange={(e) => patch(i, { fromMonth: e.target.value })}
              />
              <Input
                label="From day"
                type="number"
                min={1}
                max={31}
                value={d.fromDay}
                onChange={(e) => patch(i, { fromDay: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Select
                label="To month"
                options={MONTH_OPTIONS}
                value={d.toMonth}
                onChange={(e) => patch(i, { toMonth: e.target.value })}
              />
              <Input
                label="To day"
                type="number"
                min={1}
                max={31}
                value={d.toDay}
                onChange={(e) => patch(i, { toDay: e.target.value })}
              />
            </div>
          </div>

          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
            <Input
              label="Adjustment (%)"
              type="number"
              step="0.1"
              value={d.pct}
              onChange={(e) => patch(i, { pct: e.target.value })}
              placeholder="optional"
            />
            <Input
              label="Absolute (€)"
              type="number"
              step="0.01"
              value={d.abs}
              onChange={(e) => patch(i, { abs: e.target.value })}
              placeholder="optional"
            />
            <Input
              label="Min stay (nights)"
              type="number"
              min={1}
              value={d.minStay}
              onChange={(e) => patch(i, { minStay: e.target.value })}
              placeholder="optional"
            />
          </div>

          {/* The price band for these dates. Empty means the apartment's own
              bound still applies; a value here replaces that side, so a season
              may price above the apartment maximum (OH-142). */}
          <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input
              label="Min price (€)"
              type="number"
              min={0}
              step="0.01"
              value={d.minPrice}
              onChange={(e) => patch(i, { minPrice: e.target.value })}
              placeholder="apartment min"
            />
            <Input
              label="Max price (€)"
              type="number"
              min={0}
              step="0.01"
              value={d.maxPrice}
              onChange={(e) => patch(i, { maxPrice: e.target.value })}
              placeholder="apartment max"
            />
          </div>

          <div className="mt-3 flex justify-end">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => commit(drafts.filter((_, idx) => idx !== i))}
            >
              <Trash2 className="h-4 w-4" />
              Remove season
            </Button>
          </div>
        </div>
      ))}

      <div>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() =>
            commit([
              ...drafts,
              {
                fromMonth: '6',
                fromDay: '1',
                toMonth: '8',
                toDay: '31',
                pct: '0',
                abs: '',
                minStay: '',
                minPrice: '',
                maxPrice: '',
              },
            ])
          }
        >
          <Plus className="h-4 w-4" />
          Add season
        </Button>
      </div>
    </div>
  );
}
